import type { HomeRoutineTab } from "../../../server/modules/homecare/home-routine";
import type { ChecklistView } from "../../../server/modules/homecare/orientation";
import { refuse } from "../clients/forms";
import { api, el, toast } from "../../ui/dom";
import { photoImg, preparePhoto } from "../../ui/face";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, muted, table } from "../../ui/record";
import { isPhoneLike, uploadFile } from "../../ui/upload";
import { openViewer } from "../../ui/viewer";

// The patient's Home and routine tab (brief 1E-17, mockup v76 homeTab): the Photo card (Take
// photo or Retake, Photo consent signed; the face is found and the photo cropped in the browser),
// Conversation starters (chips with ×, Add), and the guide by the company's sections (version, who,
// review date; each item with its photo, who added it and when, the pencil; Add; Print). An item a
// caregiver marked in the field app shows "Changed? Check with the nurse" until the nurse changes
// it or keeps it. Every change saves a new version. Brief 1E-18 (mockup v76, v80): Reviewed, no
// changes (moves the review date), and the orientation card: the checklist built from the guide,
// what the orienting person and the new caregiver each checked, their comments, "n to review",
// and the manager's Reviewed with both · mark oriented (with a reason).

type Tab = HomeRoutineTab & { error?: string; file?: { id: string; name: string } };
interface Orientations {
  canClose: boolean;
  orientations: ChecklistView[];
  error?: string;
}
type Item = Tab["sections"][number]["items"][number];

const photoSrc = (patientId: string, fileId: string) =>
  `/api/patients/${patientId}/photos/${fileId}`;

/** A file picker for a photo: the camera on a phone, the file picker elsewhere. */
function pickPhoto(onPicked: (f: File) => void) {
  const input = el("input", {
    type: "file",
    accept: "image/jpeg,image/png,image/webp,image/heic",
    hidden: true,
    ...(isPhoneLike() ? { capture: "environment" } : {}),
  });
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    input.remove();
    if (f) onPicked(f);
  });
  document.body.append(input);
  input.click();
}

/** Makes the photo in the browser and uploads it to the patient's files. */
async function uploadPhoto(patientId: string, f: File, kind: "patient" | "guide") {
  const made = await preparePhoto(f, kind);
  if (!made.ok) {
    toast("That photo wasn't saved", made.error);
    return null;
  }
  const up = await uploadFile(made.file, { kind: "patient", id: patientId });
  if (!up.ok) {
    toast("That photo wasn't saved", up.error);
    return null;
  }
  return { id: up.file.id, faceFound: made.faceFound };
}

export async function homeRoutineTab(host: HTMLElement, patientId: string, onChange: () => void) {
  const res = await api<Tab>("GET", `/api/patients/${patientId}/routine`);
  if (!res.ok) {
    host.replaceChildren(el("p", { class: "note" }, "Home and routine isn't available."));
    return { reload: async () => undefined };
  }
  let t: Tab = res.body;
  // The orientation card reads the schedule: people who don't see visits don't get it.
  const loadOrientations = async () => {
    const r = await api<Orientations>("GET", `/api/patients/${patientId}/orientations`);
    return r.ok ? r.body : null;
  };
  let ori = await loadOrientations();

  const took = (r: { ok: boolean; body: Tab }, ok?: string, sub?: string) => {
    if (!r.ok) {
      toast("That wasn't saved", r.body.error);
      return false;
    }
    const photoWas = t.photo.fileId;
    t = r.body;
    draw();
    if (ok) toast(ok, sub);
    // The record's head shows the photo.
    if (t.photo.fileId !== photoWas) onChange();
    return true;
  };
  const send = async (method: string, path: string, body?: unknown) =>
    api<Tab>(method, `/api/patients/${patientId}${path}`, body);

  // ---- Photo ------------------------------------------------------------------------------------------

  function photoCard(): HTMLElement {
    const p = t.photo;
    const face = p.fileId
      ? photoImg(photoSrc(patientId, p.fileId), `Photo of ${t.patient.name}`, "rphoto")
      : el(
          "span",
          { class: "rphoto empty muted", role: "img", "aria-label": "No photo yet" },
          icon("device-camera"),
        );
    const phone = isPhoneLike();
    const take = el(
      "button",
      { type: "button", class: "btn sm" },
      icon(phone ? "device-camera" : "upload"),
      p.fileId ? (phone ? " Retake" : " Change photo") : phone ? " Take photo" : " Upload photo",
    );
    take.disabled = !t.canEdit;
    take.addEventListener("click", () => {
      if (p.consentRequired && !p.consent)
        return toast("Photo consent first", "Tick Photo consent signed");
      pickPhoto(async (f) => {
        take.disabled = true;
        const up = await uploadPhoto(patientId, f, "patient");
        take.disabled = false;
        if (!up) return;
        took(
          await send("PUT", "/photo", { fileId: up.id }),
          "Photo saved",
          up.faceFound ? "Face found and cropped" : "Cropped to the center (no face found)",
        );
      });
    });
    const consent = el("input", {
      type: "checkbox",
      id: "rconsent",
      checked: !!p.consent,
      disabled: !t.canEdit,
    });
    consent.addEventListener("change", async () => {
      took(
        await send("PUT", "/photo-consent", { signed: consent.checked }),
        consent.checked ? "Consent noted" : "Consent removed",
      );
    });
    return el(
      "section",
      { class: "card", "aria-label": "Photo" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("person"), " Photo"),
          el("span", { class: "small muted" }, "Shows on visits and in the field app"),
        ),
      ),
      el(
        "div",
        { style: "display:flex;gap:14px;align-items:center" },
        face,
        el(
          "div",
          { style: "display:flex;flex-direction:column;gap:6px" },
          take,
          el(
            "label",
            { class: "ptask", style: "margin:0", for: "rconsent" },
            consent,
            " Photo consent signed",
          ),
          p.consent
            ? el("span", { class: "small muted" }, `${p.consent.on} · ${p.consent.by}`)
            : null,
          el("span", { class: "hint" }, "The face is found and cropped."),
        ),
      ),
    );
  }

  // ---- Conversation starters ------------------------------------------------------------------------------

  function talkCard(): HTMLElement {
    const input = el("input", {
      id: "rtalk",
      type: "text",
      placeholder: "Loves the Ravens",
      maxlength: "40",
      style: "max-width:240px",
    });
    const add = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add");
    const submit = async () => {
      const v = input.value.trim();
      if (!v) return toast("Type a conversation starter");
      if (took(await send("POST", "/talk", { body: v }))) {
        (document.getElementById("rtalk") as HTMLInputElement | null)?.focus();
      }
    };
    add.addEventListener("click", () => void submit());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void submit();
      }
    });
    return el(
      "section",
      { class: "card", "aria-label": "Conversation starters" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("comment-discussion"), " Conversation starters"),
          el("span", { class: "small muted" }, "Enough to start a conversation"),
        ),
      ),
      el(
        "div",
        { class: "linkrow" },
        ...(t.talk.length
          ? t.talk.map((x) => {
              const rm = el(
                "button",
                { type: "button", class: "tox", "aria-label": `Remove ${x.body}` },
                "×",
              );
              rm.disabled = !t.canEdit;
              rm.addEventListener(
                "click",
                async () => void took(await send("POST", `/talk/${x.id}/remove`)),
              );
              return el("span", { class: "chip info" }, x.body, " ", rm);
            })
          : [muted("Nothing yet.")]),
      ),
      t.canEdit
        ? el(
            "div",
            { class: "actions", style: "margin-top:8px" },
            el("label", { class: "sr", for: "rtalk" }, "Add a conversation starter"),
            input,
            add,
          )
        : null,
    );
  }

  // ---- The guide ----------------------------------------------------------------------------------------

  function itemModal(item: Item | null, sectionId: string | null) {
    const sections = t.sections;
    let photo: string | null = item?.photoFileId ?? null;
    const sel = el(
      "select",
      { id: "ri-s" },
      ...sections.map((s) =>
        el("option", { value: s.id, selected: s.id === (sectionId ?? sections[0]?.id) }, s.name),
      ),
    );
    const text = el("textarea", { id: "ri-t", maxlength: "200" }, item?.body ?? "");
    const preview = el("div", { class: "actions" });
    const drawPreview = () =>
      preview.replaceChildren(
        ...(photo ? [photoImg(photoSrc(patientId, photo), "Photo for this item", "rtile")] : []),
        addPhoto,
      );
    const addPhoto = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("device-camera"),
      " Take or add a photo",
    );
    addPhoto.addEventListener("click", () =>
      pickPhoto(async (f) => {
        addPhoto.disabled = true;
        const up = await uploadPhoto(patientId, f, "guide");
        addPhoto.disabled = false;
        if (!up) return;
        photo = up.id;
        drawPreview();
        toast("Photo added");
      }),
    );
    drawPreview();
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
    const m = openModal({
      title: item ? "Change this item" : "Add to Home and routine",
      sub: "A change saves as a new version.",
      body: [
        el(
          "form",
          { class: "form", style: "max-width:none" },
          el("div", { class: "field" }, el("label", { for: "ri-s" }, "Section"), sel),
          el("div", { class: "field" }, el("label", { for: "ri-t" }, "What to know"), text),
          preview,
        ),
      ],
      actions: [save, cancel],
    });
    cancel.addEventListener("click", () => m.close());
    save.addEventListener("click", async () => {
      const body = text.value.trim();
      if (!body) return toast("Type what to know");
      save.disabled = true;
      const r = item
        ? await send("PUT", `/routine/items/${item.id}`, {
            sectionId: sel.value,
            body,
            photoFileId: photo,
          })
        : await send("POST", "/routine/items", { sectionId: sel.value, body, photoFileId: photo });
      save.disabled = false;
      if (!r.ok) {
        m.note.hidden = false;
        m.note.replaceChildren(r.body.error ?? "That wasn't saved.");
        return;
      }
      m.close();
      took(r, "Saved", `Version ${r.body.guide?.version ?? 1}`);
    });
    text.focus();
  }

  function itemRow(it: Item, sectionId: string): HTMLElement {
    const keep =
      it.marked && t.canEdit
        ? (() => {
            const b = el("button", { type: "button", class: "btn sm" }, icon("check"), " Keep");
            b.setAttribute("data-tip", "Keep it as it is");
            b.addEventListener(
              "click",
              async () =>
                void took(await send("POST", `/routine/items/${it.id}/keep`), "Kept as it is"),
            );
            return b;
          })()
        : null;
    return el(
      "div",
      { class: "ritem" },
      it.photoFileId
        ? photoImg(photoSrc(patientId, it.photoFileId), `Photo: ${it.body}`, "rtile")
        : null,
      el(
        "div",
        { style: "flex:1;min-width:0" },
        el("div", {}, it.body),
        el(
          "div",
          { class: "small muted" },
          `${it.addedBy} · ${it.addedOn}`,
          it.marked ? ` · marked by ${it.marked.by} ${it.marked.on}` : "",
        ),
      ),
      it.marked ? chip(it.marked.words, "warn") : null,
      keep,
      t.canEdit ? ib("pencil", "Change", () => itemModal(it, sectionId)) : null,
    );
  }

  function guideCard(): HTMLElement {
    const g = t.guide;
    const add = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add");
    add.addEventListener("click", () => itemModal(null, null));
    const print = el(
      "button",
      { type: "button", class: "btn sm ghost" },
      icon("download"),
      " Print",
    );
    const reviewed = el(
      "button",
      { type: "button", class: "btn sm ghost" },
      icon("check"),
      " Reviewed, no changes",
    );
    reviewed.addEventListener("click", async () => {
      reviewed.disabled = true;
      const r = await send("POST", "/routine/reviewed");
      reviewed.disabled = false;
      took(r, "Reviewed, no changes", `Next review by ${r.body.guide?.reviewBy ?? ""}`);
    });
    print.addEventListener("click", async () => {
      print.disabled = true;
      const r = await send("POST", "/routine/print");
      print.disabled = false;
      if (!r.ok) return toast("That wasn't printed", r.body.error);
      if (r.body.file)
        void openViewer({ file: r.body.file, document: `Home and routine v${g?.version ?? 1}` });
    });
    const line = g
      ? `Version ${g.version} · ${g.changedOn} · ${g.changedBy} · review by ${g.reviewBy}`
      : null;
    return el(
      "section",
      { class: "card", "aria-label": "Home and routine" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("home"), " Home and routine"),
          line ? el("span", { class: "small muted" }, line) : null,
          g?.reviewOverdue ? el("span", {}, " ", chip("Review due", "warn")) : null,
        ),
        el(
          "span",
          { class: "actions" },
          ...(t.canEdit ? [add] : []),
          ...(t.canEdit && g ? [reviewed, print] : []),
        ),
      ),
      ...t.sections.flatMap((s) => [
        el("h3", { class: "rsec" }, icon(s.icon), ` ${s.name}`),
        ...(s.items.length
          ? s.items.map((it) => itemRow(it, s.id))
          : [
              el(
                "p",
                { class: "small muted", style: "margin:0" },
                `Nothing yet for ${t.patient.first}.`,
              ),
            ]),
      ]),
    );
  }

  // ---- Orientation (brief 1E-18) -----------------------------------------------------------------------

  function checkChip(on: boolean) {
    return on ? chip("Checked", "ok") : chip("Not checked", "plain");
  }

  function closeModal(o: ChecklistView) {
    const why = el("textarea", { id: "or-why", maxlength: "300" });
    const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Mark oriented");
    const m = openModal({
      title: "Reviewed with both · mark oriented",
      sub: `${o.who} joins ${t.patient.first}'s care team.`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el(
            "div",
            { class: "field" },
            el("label", { for: "or-why" }, "What was reviewed with both"),
            why,
          ),
        ),
      ],
      actions: [go],
    });
    why.focus();
    go.addEventListener("click", async () => {
      if (!why.value.trim()) return refuse(m.note, "Say what was reviewed with both.");
      go.disabled = true;
      const r = await api<Orientations>(
        "POST",
        `/api/patients/${patientId}/orientations/${o.id}/close`,
        { reason: why.value },
      );
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error ?? "That wasn't saved.");
      m.close();
      ori = r.body;
      draw();
      toast("Oriented", `${o.who} · on the care team`);
    });
  }

  function orientationCard(o: ChecklistView): HTMLElement {
    const comment = (i: ChecklistView["items"][number]) =>
      el(
        "span",
        { class: "small" },
        [i.orientingComment, i.newComment].filter(Boolean).join(" · "),
      );
    const close =
      ori?.canClose && o.status === "to_review"
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm pri" },
              icon("check"),
              " Reviewed with both · mark oriented",
            );
            b.addEventListener("click", () => closeModal(o));
            return el("div", { class: "actions", style: "margin-top:8px" }, b);
          })()
        : null;
    return el(
      "section",
      { class: "card", "aria-label": `Orientation · ${o.who}` },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("people"), ` Orientation · ${o.who}`),
          el("span", { class: "small muted" }, `With ${o.by} · ${o.date} · built from the guide`),
        ),
        chip(o.chip.words, o.chip.level),
      ),
      o.items.length
        ? table(
            [
              { h: "Shown and learned", f: (i) => i.body },
              { h: "Orienting", f: (i) => checkChip(i.orienting) },
              { h: "New caregiver", f: (i) => checkChip(i.new) },
              { h: "Comment", f: comment },
            ],
            o.items,
            undefined,
            `Orientation checklist for ${o.who}`,
          )
        : muted("Nothing in the guide to check."),
      o.closed ? el("p", { class: "small muted" }, `Reviewed with both: ${o.closed.reason}`) : null,
      close,
    );
  }

  function draw() {
    host.replaceChildren(
      el("div", { class: "grid2", style: "align-items:start" }, photoCard(), talkCard()),
      guideCard(),
      ...(ori?.orientations ?? []).map(orientationCard),
    );
  }
  draw();

  return {
    reload: async () => {
      const r = await api<Tab>("GET", `/api/patients/${patientId}/routine`);
      if (r.ok) {
        t = r.body;
        ori = await loadOrientations();
        if (host.isConnected) draw();
      }
    },
  };
}
